import React from "react";
import { useState, useEffect, useMemo } from "react";

import {
	Stack,
	Typography,
	Box,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Table,
	CircularProgress,
	MenuItem,
	TablePagination,
	Breadcrumbs,
	Divider,
	InputAdornment,
	IconButton,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { StyledSelectTextBox, StyledTextBox, StyledDatePickerBox } from "../../../components/TextBox";
import { StyledTableContainer, StyledTableCell } from "../../../components/Table";
import CloseIcon from "@mui/icons-material/Close";
import Moment from "moment";
import { LoadingBox } from "../../../components/Box";
import { GetBusinessList } from "../../../services/business.services";
import { GetKpiStatistics, GetLedgerSearch, GetLedgerSummary } from "../../../services/ledger.services";
import LedgerSummaryTable from "./LedgerSummaryTable";
import LedgerSummaryBreakdown from "./LedgerSummayBreakdown";
import { formatCurrency, formatLabel } from "../../../utils/utilityFunctions";
import moment from "moment";
import { sortByCreatedDateTime } from "../../../utils/sortBy";
import ExcelExport from "../../../components/ExcelExport";
import LedgerSummaryStatistics from "./LedgerSummeryStatistics";

const financeColors = [
	"E3F2FD", // light blue
	"E8F5E9", // light green
	"FFF3E0", // light orange
	"F3E5F5", // light purple
	"FBE9E7", // light peach
	"ECEFF1", // cool gray
];

export default function LedgerSummary(props) {
	const [startDate, setStartDate] = useState(
		Moment().subtract(7, "days").toDate()
	);
	const [isLoading, setIsLoading] = useState(false);
	const [endDate, setEndDate] = useState(Moment().toDate());
	const [businessId, setBusinessId] = useState("");
	const [ledgerDataBreakdown, setLedgerDataBreakdown] = useState(null);
	const [ledgers, setLedgers] = useState(null);
	const [businessList, setBusinessList] = useState(null);
	const [selectedLedger, setSelectedLedger] = useState(null);
	const [ledgerStatistics, setLedgerStatistics] = useState(null);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [searchTerm, setSearchTerm] = useState('');
	const [dateError, setDateError] = useState(false);

	// Helper function to convert RGB to hex
	const rgbToHex = (rgb) => `#${rgb}`;

	useEffect(() => {
		setBusinessId("");
	}, [props.currency])

	useEffect(() => {
		(async () => {
			try {
				await GetBusinessList().then((data) => {
					setBusinessList(data);
				});
			} catch (error) {
				throw error;
			}
		})();
	}, []);

	useEffect(() => {
		(async () => {
			setIsLoading(true);
			try {
				const payload = {
					startDate: Moment(startDate).format('YYYY-MM-DD'),
					endDate: Moment(endDate).format('YYYY-MM-DD'),
					businessId: businessId === "" ? undefined : businessId,
					currency: props.currency,
				}

				await GetLedgerSummary(payload).then((data) => {
					setLedgers(data);
				});
			} catch (error) {
				throw error;
			} finally {
				setIsLoading(false);
			}
		})();
	}, [startDate, endDate, businessId, props.currency]);

	useEffect(() => {
		(async () => {
			if (!selectedLedger) return;
			setIsLoading(true);
			try {
				const payload = {
					startDate: Moment(startDate).format('YYYY-MM-DD'),
					endDate: Moment(endDate).format('YYYY-MM-DD'),
					businessId: businessId === "" ? undefined : businessId,
					ledgerId: selectedLedger?.ledgerId,
					currency: props.currency,
				}

				await GetLedgerSearch(payload).then((data) => {
					setLedgerDataBreakdown(data.data);
				});

				await GetKpiStatistics(payload).then((data) => {
					setLedgerStatistics(data);
				});
			} catch (error) {
				throw error;
			} finally {
				setIsLoading(false);
			}
		})();
	}, [startDate, endDate, businessId, selectedLedger, props.currency]);

	// Filter data by orderId
	const filteredData = useMemo(() => {
		if (!searchTerm.trim()) return ledgerDataBreakdown;
		return ledgerDataBreakdown.filter((item) =>
			item.orderId?.toString().toLowerCase().includes(searchTerm.toLowerCase())
		);
	}, [ledgerDataBreakdown, searchTerm]);

	// Paginate filtered data
	const paginatedData = useMemo(() => {
		const start = page * rowsPerPage;
		const end = start + rowsPerPage;
		return filteredData?.slice(start, end);
	}, [filteredData, page, rowsPerPage]);

	if (!ledgers || !businessList) return (
		<LoadingBox>
			<CircularProgress />
		</LoadingBox>
	);

	const setSelectedLedgerCallback = (data) => {
		setSelectedLedger(data);
	};

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(+event.target.value);
		setPage(0);
	};

	const exportSummeryBreakDownData = sortByCreatedDateTime(ledgerDataBreakdown)
		.map((row) => {
			const business = businessList?.find(b => b.id === row.businessId);
			const businessName = business ? `${business.name} (${row.businessId})` : row.businessId;
			return {
				"Date": moment(row.createdDate).format("L"),
				"Transaction ID": row.transactionId,
				"Business ID": businessName,
				"Order ID": row.orderId,
				"Description": row.description,
				"Debit ($)": row.entryType === 'D' ? Number(row.amount?.toFixed(2)) : '',
				"Credit ($)": row.entryType === 'C' ? Number(row.amount?.toFixed(2)) : '',
				"Marketplace Payment Status": '',
			};
		});


	const groupedLedgers = ledgers.reduce((acc, ledger) => {
		// Trim the group name and normalize spaces
		const groupName = ledger.group?.trim().replace(/\s+/g, ' ') || "Others";
		if (!acc[groupName]) {
			acc[groupName] = { items: [], totals: { debit: 0, credit: 0, balance: 0 } };
		}
		acc[groupName].items.push(ledger);

		// Add to totals
		acc[groupName].totals.debit += ledger.totalDebitAmount || 0;
		acc[groupName].totals.credit += ledger.totalCreditAmount || 0;
		acc[groupName].totals.balance += ledger.balance || 0;

		return acc;
	}, {});


	const groupColors = {};
	let colorIndex = 0;

	Object.keys(groupedLedgers).forEach((group) => {
		groupColors[group] = financeColors[colorIndex % financeColors.length];
		colorIndex++;
	});

	// Build export data using the same groupedLedgers to ensure consistency
	const exportSummeryData = [];

	Object.entries(groupedLedgers).forEach(([groupName, groupData]) => {
		// Group totals row - include groupName as metadata for coloring
		exportSummeryData.push({
			"Ledger Name": `${groupName} (Group Totals)`,
			"Debit ($)": Number(groupData.totals.debit.toFixed(2)),
			"Credit ($)": Number(groupData.totals.credit.toFixed(2)),
			"Balance ($)": Number(groupData.totals.balance.toFixed(2)),
			_isGroupTotal: true, // Flag to identify group total rows for coloring
			_groupName: groupName, // Metadata for color mapping
		});

		// Each ledger row
		groupData.items.forEach((row) => {
			exportSummeryData.push({
				"Ledger Name": formatLabel(row.name),
				"Debit ($)": Number(row.totalDebitAmount?.toFixed(2)),
				"Credit ($)": Number(row.totalCreditAmount?.toFixed(2)),
				"Balance ($)": Number(row.balance?.toFixed(2)),
				_groupName: groupName, // Metadata for color mapping (not colored)
			});
		});

		// Empty row for spacing
		exportSummeryData.push({});
	});



	return (
		<>
			<Breadcrumbs separator="›" aria-label="breadcrumb">
				<Typography variant="h4" >General Ledger</Typography>
				<Typography variant="h4">{props.currency}</Typography>
				<Typography variant="h4" sx={{ cursor: 'pointer' }} onClick={() => setSelectedLedger(null)}>Ledger Summary</Typography>
				{selectedLedger && <Typography variant="h4">{formatLabel(selectedLedger.name)}</Typography>}
			</Breadcrumbs>
			<br></br>
			<Box
				sx={{
					background: "#FFFFFF",
					boxShadow: "0px 2px 8px rgba(3, 40, 120, 0.18)",
					borderRadius: "18px",
					p: 5,
				}}
			>
				<Stack spacing={2} direction="row" justifyContent="space-between" alignItems='center'>
					<Stack direction="row" spacing={2}>
						<Stack>
							<Stack direction="row" spacing={2}>
						<LocalizationProvider dateAdapter={AdapterDateFns}>
							<Stack spacing={2}>
								<Typography variant="body2">Start Date</Typography>
								<DatePicker
									openTo="day"
									views={["year", "month", "day"]}
									value={startDate}
									onChange={(newValue) => {
										setStartDate(newValue);
										if (newValue > endDate) {
											setDateError(true);
										} else {
											setDateError(false);
										}
									}}
									maxDate={endDate}
									renderInput={(params) => (
										<StyledDatePickerBox {...params} size="small" />
									)}
								/>
							</Stack>
							<Stack spacing={2}>
								<Typography variant="body2">End Date</Typography>
								<DatePicker
									openTo="day"
									views={["year", "month", "day"]}
									value={endDate}
									onChange={(newValue) => {
										setEndDate(newValue);
										if (newValue < startDate) {
											setDateError(true);
										} else {
											setDateError(false);
										}
									}}
									minDate={startDate}
									renderInput={(params) => (
										<StyledDatePickerBox {...params} size="small" />
									)}
								/>
							</Stack>
						</LocalizationProvider>
						</Stack>
						{dateError && (
							<Typography variant="caption" sx={{ color: "#C62828" }}>
								Date Range is Invalid
							</Typography>
						)}
						</Stack>
						<Stack spacing={2} >
							<Typography variant="body2">Select Business</Typography>
							<StyledSelectTextBox
								value={businessId}
								onChange={(e) => setBusinessId(e.target.value)}
								displayEmpty
							>
								<MenuItem value="">All</MenuItem>
								{businessList?.filter(value => value.businessStatus === 'APPROVED' && value.currency === props.currency).map((row, index) => (
									<MenuItem value={row.id} key={index}>{row.name}</MenuItem>
								))}
							</StyledSelectTextBox>
						</Stack>
						
					</Stack>
					<ExcelExport
						filename={
							`${selectedLedger ? formatLabel(selectedLedger.name) + ' Breakdown Summary' : 'Ledger Summary'} from ${moment(startDate).format("MMM D, y")} to ${moment(endDate).format("MMM D, y")}`
						}
						excelData={selectedLedger ? exportSummeryBreakDownData : exportSummeryData}
						colorMap={selectedLedger ? null : groupColors}
					/>
				</Stack>
				{ledgerStatistics && selectedLedger && <>
					<br></br>
					<LedgerSummaryStatistics selectedLedger={selectedLedger.name} ledgerStatistics={ledgerStatistics} />
				</>}

				<br></br>
				<Divider></Divider>
				<br></br>
				{selectedLedger && (
					<Stack spacing={2} alignItems="flex-start">
						<Typography variant="body2">Enter a Order ID</Typography>
						<StyledTextBox
							placeholder="Search by Order ID..."
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							InputProps={{
								endAdornment: searchTerm && (
									<InputAdornment position="end">
										<IconButton
											onClick={() => setSearchTerm('')}
											edge="end"
											size="small"
										>
											<CloseIcon />
										</IconButton>
									</InputAdornment>
								),
							}}
						/>
					</Stack>
				)}
				<br></br>
				<StyledTableContainer>
					<Table sx={{ minWidth: 650 }} aria-label="simple table">
						{selectedLedger ? (
							<>
								<TableHead>
									<TableRow sx={{ backgroundColor: "#F6F6F6" }}>
										<StyledTableCell align="left">
											Date
										</StyledTableCell>
										<StyledTableCell align="left">
											Transaction ID
										</StyledTableCell>
										<StyledTableCell align="center">
											Business ID
										</StyledTableCell>
										<StyledTableCell align="right">
											Transaction Type
										</StyledTableCell>
										<StyledTableCell align="center">
											Order ID
										</StyledTableCell>
										<StyledTableCell align="left">
											Description
										</StyledTableCell>
										<StyledTableCell align="right">
											Debit
										</StyledTableCell>
										<StyledTableCell align="right">
											Credit
										</StyledTableCell>
										<StyledTableCell align="center">
											Markeplace Settlement Status
										</StyledTableCell>
									</TableRow>
								</TableHead>
								{!ledgerDataBreakdown || isLoading ? (
									<TableBody>
										<TableRow>
											<TableCell
												colSpan={9}
												align="center"
												sx={{ padding: "10%" }}
											>
												<CircularProgress />
											</TableCell>
										</TableRow>
									</TableBody>
								) : ledgerDataBreakdown?.length <= 0 ? (
									<TableBody>
										<TableRow>
											<TableCell
												colSpan={9}
												align="center"
												sx={{ padding: "10%" }}
											>
												<Typography variant="h5">
													<b>No results found for "{formatLabel(selectedLedger.name)}"</b>
												</Typography>
											</TableCell>
										</TableRow>
									</TableBody>
								) : filteredData?.length <= 0 ? (
									<TableBody>
										<TableRow>
											<TableCell
												colSpan={9}
												align="center"
												sx={{ padding: "10%" }}
											>
												<Typography variant="h5">
													<b>No results found for Order ID "{searchTerm}"</b>
												</Typography>
											</TableCell>
										</TableRow>
									</TableBody>
								) : (
									paginatedData.map((row, index) => (
										<LedgerSummaryBreakdown key={index} data={row} index={index} businessList={businessList} />
									))
								)
								}
							</>
						) : (
							<>
								<TableHead>
									<TableRow sx={{ backgroundColor: "#F6F6F6" }}>
										<StyledTableCell align="left">
											Ledger Name
										</StyledTableCell>
										<StyledTableCell align="right">
											Debit
										</StyledTableCell>
										<StyledTableCell align="right">
											Credit
										</StyledTableCell>
										<StyledTableCell align="right">
											Balance
										</StyledTableCell>
										<StyledTableCell align="center">
											View
										</StyledTableCell>
									</TableRow>
								</TableHead>
								{isLoading ? (
									<TableBody>
										<TableRow>
											<TableCell
												colSpan={9}
												align="center"
												sx={{ padding: "10%" }}
											>
												<CircularProgress />
											</TableCell>
										</TableRow>
									</TableBody>
								) : ledgers.length <= 0 ? (
									<TableBody>
										<TableRow>
											<TableCell
												colSpan={9}
												align="center"
												sx={{ padding: "10%" }}
											>
												<Typography variant="h5">
													<b>No results found</b>
												</Typography>
											</TableCell>
										</TableRow>
									</TableBody>
								) : (Object.entries(groupedLedgers).map(([groupName, groupData]) => (
									<React.Fragment key={groupName}>
										<TableBody>
											{/* Group header row */}
											<TableRow sx={{ backgroundColor: rgbToHex(groupColors[groupName]) }}>
												<TableCell align="left">
													<Typography variant="body1" sx={{ fontWeight: "bold" }}>
														{groupName}
													</Typography>
												</TableCell>
												<TableCell align="right" sx={{ fontWeight: "bold" }}>
													{formatCurrency(groupData.totals.debit)}
												</TableCell>
												<TableCell align="right" sx={{ fontWeight: "bold" }}>
													{formatCurrency(groupData.totals.credit)}
												</TableCell>
												<TableCell align="right" sx={{ fontWeight: "bold" }}>
													{formatCurrency(groupData.totals.balance)}
												</TableCell>
												<TableCell />
											</TableRow>
										</TableBody>
										{/* Group ledgers */}
										{groupData.items.map((row, index) => (
											<LedgerSummaryTable
												key={row.ledgerId}
												data={row}
												index={index}
												setSelectedLedgerCallback={setSelectedLedgerCallback}
											/>
										))}
									</React.Fragment>
								))
								)
								}
							</>
						)}

					</Table>
				</StyledTableContainer>
				{ledgerDataBreakdown?.length > 0 && selectedLedger && <TablePagination
					rowsPerPageOptions={[10, 25, 100]}
					component="div"
					count={filteredData?.length}
					rowsPerPage={rowsPerPage}
					page={page}
					onPageChange={handleChangePage}
					onRowsPerPageChange={handleChangeRowsPerPage}
				/>
				}

			</Box>
		</>
	);
}